/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import { ChangeEvent, useState } from "react";
import { useSelector } from "react-redux";
import {
    Button,
    Col,
    FormGroup,
    Input,
    Label,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
    Row,
    Table,
} from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faEdit, faTrash, faSync, faSave, faTimes } from "@fortawesome/pro-regular-svg-icons";

import { InputType } from "reactstrap/types/lib/Input";
import { Loading } from "../../../Helper/Loading";
import {
    RegisterConfig,
    RegisterConfigCreate,
    useDeviceRegisters,
    useSyncStatus,
} from "../../../../hooks/useDeviceRegisters";
import { useImportExport } from "../../../Helper/ImportExportModal/useImportExport";
import { ImportExportModal } from "../../../Helper/ImportExportModal";

interface RootState {
    devices: {
        current?: {
            UID?: string;
            type?: string;
        };
    };
}

interface FormData extends RegisterConfigCreate {
    description: string;
    model: string;
    factor: number;
    min_value: number | null;
    max_value: number | null;
}

const defaultFormData: FormData = {
    room: "",
    ip: "",
    port: 502,
    read_write_mode: "read",
    key: "",
    label: "",
    description: "",
    register: "",
    type: "uint16",
    model: "",
    factor: 1.0,
    min_value: null,
    max_value: null,
};

const registerTypes = [
    { value: "uint16", label: "UInt16 - Default" },
    { value: "int16", label: "Int16" },
    { value: "int32", label: "Int32" },
    { value: "uint32", label: "UInt32" },
    { value: "float32", label: "Float32" },
    { value: "float64", label: "Float64" },
    { value: "string", label: "String" },
    { value: "bool", label: "Boolean" },
] as const;

const readWriteModes = [
    { value: "read", label: "Read" },
    { value: "write", label: "Write" },
    { value: "read_write", label: "Read/Write" },
] as const;

// Parses a numeric string, returning null for empty/missing values and for
// anything that doesn't parse to a finite number (e.g. "abc", a stray "-",
// or a malformed imported cell) instead of letting NaN leak into form state.
const parseFiniteOrNull = (value: unknown): number | null => {
    if (value === "" || value === null || value === undefined) return null;
    const parsed = parseFloat(value as string);
    return Number.isFinite(parsed) ? parsed : null;
};

const createRegisterFields = [
    {
        name: "room",
        label: "Room",
        required: true,
        description: "Room is immutable after creation.",
        placeholder: "Enter room name",
        enableReadOnly: true,
        formatter: (value) => String(value ?? ""),
    },
    {
        name: "ip",
        label: "IP Address",
        required: true,
        cols: 8,
        placeholder: "192.168.1.100"
    },
    {
        name: "port",
        label: "Port",
        description: "Port",
        required: true,
        defaultValue: defaultFormData.port,
        formatter: (value) => parseInt(value, 10) || 0,
        cols: 4,
        inputProps: {
            min: 1,
            max: 65535,
        },
        type: "number",
    },
    {
        name: "key",
        label: "Key",
        required: true,
        unique: true,
        placeholder: "Unique register key"
    },
    {
        name: "label",
        label: "Label",
        required: true,
        placeholder: "Display label"
    },
    {
        name: "register",
        label: "Register address",
        description: "Register address is immutable after creation.",
        required: true,
        placeholder: "e.g., 40001",
        enableReadOnly: true,
        formatter: (value) => String(value ?? ""),
    },
    {
        name: "type",
        label: "Data type",
        description: "Data type",
        required: true,
        defaultValue: defaultFormData.type,
        type: "select",
        options: registerTypes
    },
    {
        name: "read_write_mode",
        label: "Read/Write Mode",
        description: "Read/Write Mode",
        required: true,
        defaultValue: defaultFormData.read_write_mode,
        type: "select",
        options: readWriteModes,
    },
    {
        name: "model",
        label: "Sensor model",
        description: "Sensor manufacturer/model for tracking reliability.",
        placeholder: "e.g., Sensirion SCD30, Vaisala GMP252",
    },
    {
        name: "factor",
        label: "Scaling factor",
        description: "Raw value multiplier (e.g., 0.01, 0.1, 1.0)",
        required: true,
        defaultValue: defaultFormData.factor,
        formatter: (value) => parseFloat(value) || 1.0,
        placeholder: "1.0",
        inputProps: {
            step: "0.01",
            min: "0",
        }
    },
    {
        name: "min_value",
        label: "Min Value",
        description: "Optional lower bound of this sensor's value range. Also used as the graph y-axis minimum. Leave empty to auto-scale.",
        formatter: parseFiniteOrNull,
        placeholder: "e.g., 0",
        type: "number",
        inputProps: {
            step: "any",
        }
    },
    {
        name: "max_value",
        label: "Max Value",
        description: "Optional upper bound of this sensor's value range. Also used as the graph y-axis maximum. Leave empty to auto-scale.",
        formatter: parseFiniteOrNull,
        placeholder: "e.g., 100",
        type: "number",
        inputProps: {
            step: "any",
        }
    },
    {
        name: "description",
        label: "Description",
        description: "Description",
        type: "textarea",
        placeholder: "Optional description",
        inputProps: {
            rows: 3,
        }
    },
];

const tableColumns = createRegisterFields?.filter((f) => f.name !== "description");

function getButtonText(isCreating: boolean, isUpdating: boolean, isEditing: RegisterConfig | null): string {
    if (isCreating || isUpdating) return "Saving...";
    if (isEditing) return "Update";
    return "Create";
}

export default function RegisterConfigTab() {
    const device = useSelector((state: RootState) => state.devices.current);
    const clientId = device?.UID;

    const {
        data,
        isLoading,
        isError,
        createMutation,
        updateMutation,
        deleteMutation,
        syncMutation,
        deviceRegistersBaseEndpoint,
    } = useDeviceRegisters(clientId);
    const { data: syncStatus } = useSyncStatus(clientId);

    const [modalOpen, setModalOpen] = useState(false);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [editingRegister, setEditingRegister] = useState<RegisterConfig | null>(null);
    const [registerToDelete, setRegisterToDelete] = useState<RegisterConfig | null>(null);
    const [formData, setFormData] = useState<FormData>(defaultFormData);
    const [showGatewayWarning, setShowGatewayWarning] = useState(false);
    const isEditing = Boolean(editingRegister);

    const entityName = "deviceRegister";
    const importModalProps = useImportExport({
        entityName,
        importFields: createRegisterFields,
        affectedQueries: ["deviceRegisters", "all-deviceRegisters"],
        customEndpoint: deviceRegistersBaseEndpoint,
        excludeFields: ["id", "client", "active", "created"],
        uniqueIdentifierKey: ["register", "room", "ip"],
    });

    const openCreateModal = () => {
        setEditingRegister(null);
        setFormData(defaultFormData);
        setModalOpen(true);
    };

    const openEditModal = (register: RegisterConfig) => {
        setEditingRegister(register);
        setFormData(createRegisterFields.reduce((acc, curr) => {
            acc[curr.name] = register[curr.name] ?? defaultFormData[curr.name];
            return acc;
        }, {} as FormData));
        setModalOpen(true);
    };

    const closeModal = () => {
        setModalOpen(false);
        setEditingRegister(null);
        setFormData(defaultFormData);
        setShowGatewayWarning(false);
    };

    const openDeleteModal = (register: RegisterConfig) => {
        setRegisterToDelete(register);
        setDeleteModalOpen(true);
    };

    const closeDeleteModal = () => {
        setDeleteModalOpen(false);
        setRegisterToDelete(null);
    };

    const handleFormChange = (e: ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        let parsedValue: string | number | null = value;

        if (name === "port") {
            parsedValue = parseInt(value, 10) || 0;
        } else if (name === "factor") {
            parsedValue = parseFloat(value) || 1.0;
        } else if (name === "min_value" || name === "max_value") {
            parsedValue = parseFiniteOrNull(value);
        }

        setFormData((prev) => ({
            ...prev,
            [name]: parsedValue,
        }));
    };

    const getGatewayAffectingChanges = (original: RegisterConfig) => {
        return createRegisterFields.filter((field) => {
            if (!field.required || field.name === "label") return false;

            const currentValue = formData[field.name];
            const originalValue = original[field.name];

            if (field.name === "port") {
                return Number(currentValue ?? 0) !== Number(originalValue ?? 0);
            }

            return String(currentValue ?? "") !== String(originalValue ?? "");
        });
    };

    const handleSubmit = async () => {
        try {
            if (editingRegister) {
                const changedGatewayFields = getGatewayAffectingChanges(editingRegister);

                if (changedGatewayFields.length > 0 && !showGatewayWarning) {
                    setShowGatewayWarning(true);
                    return;
                }

                await updateMutation.mutateAsync({
                    registerId: editingRegister.id,
                    data: formData,
                });
            } else {
                await createMutation.mutateAsync(formData);
            }

            closeModal();
        } catch {
            // Error handling is done in the mutation hooks
        }
    };

    const handleDelete = async () => {
        if (!registerToDelete) return;

        try {
            await deleteMutation.mutateAsync(registerToDelete.id);

            closeDeleteModal();
        } catch {
            // Error handling is done in the mutation hooks
        }
    };

    const handleSync = () => syncMutation.mutate();

    if (isLoading) {
        return <Loading />;
    }

    if (isError) {
        return (
            <div className="text-danger p-4">
                Failed to load register configurations. Please try again.
            </div>
        );
    }

    const registers = data?.results || [];

    const getSyncStatusBadge = () => {
        if (!syncStatus) return null;

        const statusConfig = {
            synced: { color: "success", text: "Synced", icon: null },
            out_of_sync: { color: "warning", text: "Out of Sync", icon: null },
            unknown: { color: "secondary", text: "Unknown", icon: null },
            no_config: { color: "info", text: "No Config", icon: null },
        };

        const config = statusConfig[syncStatus.status] || statusConfig.unknown;

        return (
            <span className={`badge bg-${config.color} ms-2`}>
                {config.icon && <FontAwesomeIcon icon={config.icon} spin className="me-1" />}
                {config.text}
            </span>
        );
    };

    const orderedFormFields = [
        ["room", "key"],
        ["ip", "port"],
        ["label", "register"],
        ["type", "read_write_mode"],
        ["model", "factor"],
        ["min_value", "max_value"],
        ["description"],
    ].map((arr) => arr.map((name) => createRegisterFields?.find((f) => f.name === name)));

    return (
        <div>
            <Row className="mb-3">
                <Col className="d-flex justify-content-end align-items-center gap-2">
                    <ImportExportModal
                        entityName={entityName}
                        customEndpoint={deviceRegistersBaseEndpoint}
                        importFields={createRegisterFields}
                        {...importModalProps}
                    />

                    <Button color="primary" onClick={openCreateModal}>
                        <FontAwesomeIcon icon={faPlus} className="me-1" />
                        Add Register
                    </Button>
                </Col>
            </Row>

            <Row>
                <Col>
                    <div css={css`min-height: 30vh;`}>
                        {registers.length === 0 ? (
                            <div className="text-center text-muted py-5">
                                No register configurations found. Click &quot;Add Register&quot; to create one.
                            </div>
                        ) : (
                            <Table hover responsive>
                                <thead>
                                    <tr>
                                        {tableColumns?.map(((col) => <th key={`th-${col.name}`}>{col.label}</th>))}
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {registers.map((reg) => (
                                        <tr key={reg.id}>
                                            {tableColumns?.map((col) => (
                                                <td
                                                    key={`td-${reg.id}-${col.name}`}
                                                    className={["model", "factor", "min_value", "max_value"].includes(col.name) ? "text-muted small" : ""}
                                                >
                                                    {reg[col.name] ?? "-"}
                                                </td>
                                            ))}

                                            <td>
                                                <Button
                                                    size="sm"
                                                    color="secondary"
                                                    className="me-1"
                                                    onClick={() => openEditModal(reg)}
                                                >
                                                    <FontAwesomeIcon icon={faEdit} />
                                                </Button>

                                                <Button
                                                    size="sm"
                                                    color="danger"
                                                    onClick={() => openDeleteModal(reg)}
                                                >
                                                    <FontAwesomeIcon icon={faTrash} />
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        )}
                    </div>
                </Col>
            </Row>

            {/* Sync Button and Status at bottom right */}
            <Row className="mt-3">
                <Col className="d-flex justify-content-end">
                    <div className="d-flex flex-column align-items-end">
                        <div className="d-flex align-items-center gap-2 mb-2">
                            {getSyncStatusBadge()}

                            <Button
                                color="secondary"
                                size="sm"
                                onClick={handleSync}
                                disabled={syncMutation.isPending || registers.length === 0}
                            >
                                <FontAwesomeIcon icon={faSync} className="me-1" />

                                {syncMutation.isPending ? "Syncing..." : "Sync to Gateway"}
                            </Button>
                        </div>

                        {syncStatus?.last_update && (
                            <small className="text-muted">
                                Last update: {new Date(syncStatus.last_update).toLocaleString()}
                            </small>
                        )}
                    </div>
                </Col>
            </Row>

            {/* Create/Edit Modal */}
            <Modal isOpen={modalOpen} toggle={closeModal} size="lg">
                <ModalHeader toggle={closeModal}>
                    {editingRegister ? "Edit Register Configuration" : "Add Register Configuration"}
                </ModalHeader>
                <ModalBody>
                    {orderedFormFields.map((row, idx) => (
                        <Row key={`row-${idx}`}>
                            {row?.map((f) => (
                                <Col md={f?.name !== "description" ? f?.cols ?? 6 : undefined} key={`form-group-${f?.name}`}>
                                    <FormGroup>
                                        <Label for={f?.name}>{f?.label} {f?.required && "*"}</Label>
                                        {f?.type === "select" ? (
                                            <Input
                                                type="select"
                                                name={f?.name}
                                                id={f?.name}
                                                value={formData[f?.name as keyof FormData] ?? ""}
                                                onChange={handleFormChange}
                                                required
                                                {...f?.inputProps && { ...f?.inputProps }}
                                            >
                                                {f.options?.map((t) => (
                                                    <option key={t.value} value={t.value}>
                                                        {t.label}
                                                    </option>
                                                ))}
                                            </Input>
                                        ) : (
                                            <Input
                                                type={(f?.type ?? "text") as InputType}
                                                name={f?.name}
                                                id={f?.name}
                                                value={formData[f?.name as keyof FormData] ?? ""}
                                                onChange={handleFormChange}
                                                placeholder={f?.placeholder}
                                                required={f?.required}
                                                readOnly={f?.enableReadOnly && isEditing}
                                                {...f?.inputProps && { ...f?.inputProps }}

                                            />
                                        )}

                                        {f?.description && isEditing && (
                                            <small className="text-muted">
                                                {f?.description}
                                            </small>
                                        )}
                                    </FormGroup>
                                </Col>
                            ))}
                        </Row>
                    ))}
                </ModalBody>

                <ModalFooter>
                    <div
                        className="alert alert-warning mb-0 me-auto"
                        role="alert"
                        style={{
                            visibility: editingRegister && showGatewayWarning && getGatewayAffectingChanges(editingRegister).length > 0
                                ? "visible"
                                : "hidden"
                        }}
                    >
                        <small>
                            <strong>Warning:</strong> Changing those fields requires gateway sync.
                        </small>
                    </div>
                    <Button
                        color="secondary"
                        onClick={closeModal}
                        disabled={createMutation.isPending || updateMutation.isPending}
                    >
                        <FontAwesomeIcon icon={faTimes} className="me-1" />
                        Cancel
                    </Button>

                    <Button
                        color="primary"
                        onClick={handleSubmit}
                        disabled={
                            createMutation.isPending
                            || updateMutation.isPending
                            || !formData.room
                            || !formData.ip
                            || !formData.key
                            || !formData.label
                            || !formData.register
                        }
                    >
                        <FontAwesomeIcon icon={faSave} className="me-1" />

                        {editingRegister && showGatewayWarning && getGatewayAffectingChanges(editingRegister).length > 0
                            ? "Confirm Update"
                            : getButtonText(createMutation.isPending, updateMutation.isPending, editingRegister)
                        }
                    </Button>
                </ModalFooter>
            </Modal>

            {/* Delete Confirmation Modal */}
            <Modal isOpen={deleteModalOpen} toggle={closeDeleteModal}>
                <ModalHeader toggle={closeDeleteModal}>Confirm Delete</ModalHeader>

                <ModalBody>
                    Are you sure you want to delete the register configuration for{" "}
                    <strong>{registerToDelete?.label}</strong> (Key: {registerToDelete?.key})?
                </ModalBody>

                <ModalFooter>
                    <Button
                        color="secondary"
                        onClick={closeDeleteModal}
                        disabled={deleteMutation.isPending}
                    >
                        Cancel
                    </Button>

                    <Button
                        color="danger"
                        onClick={handleDelete}
                        disabled={deleteMutation.isPending}
                    >
                        {deleteMutation.isPending ? "Deleting..." : "Delete"}
                    </Button>
                </ModalFooter>
            </Modal>
        </div>
    );
}
